import { useState, useEffect } from "react";
import { X, Phone, PhoneOff, Loader2, CheckCircle2, Settings } from "lucide-react";
import { api } from "../api";

/**
 * Click-to-call via LetsCall One originate.
 * Aceita `defaultExtension` para evitar fetch a /me (quando o pai ja tem a extensao).
 * Se nao fornecido, faz fetch a /me ao abrir.
 */
export function WebphoneDialog({ open, number, onClose, defaultExtension }) {
  const [extensao, setExtensao] = useState(defaultExtension || "");
  const [estado, setEstado] = useState("idle"); // "idle" | "loading" | "ok" | "erro"
  const [erroMsg, setErroMsg] = useState("");
  const [semExtensao, setSemExtensao] = useState(!defaultExtension);

  useEffect(() => {
    // Reset estado ao fechar
    if (!open) { setEstado("idle"); setErroMsg(""); return; }
    // Se defaultExtension foi passado, usa-o directamente (sem fetch)
    if (defaultExtension !== undefined) {
      setExtensao(defaultExtension || "");
      setSemExtensao(!defaultExtension);
      return;
    }
    // Sem defaultExtension: buscar extensao do perfil do utilizador
    api.get("/me").then(({ data }) => {
      const ext = data?.letscall_extension || "";
      setExtensao(ext);
      setSemExtensao(!ext);
    }).catch(() => {});
  }, [open, defaultExtension]);

  const iniciarChamada = async () => {
    setEstado("loading");
    setErroMsg("");
    try {
      const payload = { to: number };
      if (extensao.trim()) payload.agent_extension = extensao.trim();
      await api.post("/channels/letscall/originate", payload);
      setEstado("ok");
      setTimeout(onClose, 3000);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 400) {
        setErroMsg("LetsCall nao esta configurado. Vai a Definicoes > Canais > LetsCall e verifica a API Key.");
      } else if (e.response?.status === 401 || detail.includes("401")) {
        setErroMsg("API Key invalida ou sem permissoes. Reconfigura em Definicoes > Canais > LetsCall.");
      } else {
        setErroMsg(detail || "Nao foi possivel iniciar a chamada. Verifica a configuracao do LetsCall.");
      }
      setEstado("erro");
    }
  };

  const fechar = () => { onClose(); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4" data-testid="webphone-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="bg-navy text-white px-5 py-3 flex items-center justify-between">
          <span className="font-bold flex items-center gap-2"><Phone size={16} /> Ligar - {number}</span>
          <button onClick={fechar} data-testid="webphone-close" className="hover:bg-white/10 rounded p-1"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {estado === "idle" && (
            <>
              <p className="text-sm text-slate-600">
                O LetsCall vai ligar ao teu telefone primeiro e depois ligar ao numero do cliente.
              </p>
              {semExtensao && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                  <Settings size={13} className="mt-0.5 shrink-0" />
                  <span>Nao tens extensao configurada. Define em <b>Definicoes &gt; Perfil</b> para pre-preencher automaticamente.</span>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-navy mb-1 uppercase">Extensao do agente</label>
                <input
                  value={extensao}
                  onChange={(e) => setExtensao(e.target.value)}
                  placeholder="Ex: 201"
                  data-testid="webphone-extensao"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/20"
                />
                <p className="text-[11px] text-slate-400 mt-1">Configuravel em Definicoes &gt; Perfil &gt; Extensao LetsCall</p>
              </div>
              <button
                onClick={iniciarChamada}
                data-testid="webphone-iniciar"
                className="w-full bg-navy text-white font-semibold py-2.5 rounded-lg hover:opacity-90 transition flex items-center justify-center gap-2"
              >
                <Phone size={15} /> Iniciar chamada
              </button>
            </>
          )}

          {estado === "loading" && (
            <div className="flex flex-col items-center py-6 gap-3 text-slate-600">
              <Loader2 size={28} className="animate-spin text-blue" />
              <p className="text-sm">A iniciar chamada...</p>
            </div>
          )}

          {estado === "ok" && (
            <div className="flex flex-col items-center py-6 gap-3 text-emerald-700" data-testid="webphone-ok">
              <CheckCircle2 size={32} />
              <p className="text-sm font-semibold">Chamada iniciada!</p>
              <p className="text-xs text-slate-500 text-center">O teu telefone vai tocar em breve. Atende e aguarda a ligacao ao cliente.</p>
            </div>
          )}

          {estado === "erro" && (
            <div className="space-y-3">
              <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3 flex items-start gap-2" data-testid="webphone-error">
                <PhoneOff size={16} className="mt-0.5 shrink-0" />
                <span>{erroMsg}</span>
              </div>
              <button
                onClick={() => setEstado("idle")}
                className="w-full border border-slate-200 text-slate-700 text-sm font-medium py-2 rounded-lg hover:bg-slate-50 transition"
              >
                Tentar novamente
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function LigarButton({ number, className = "", label = "Ligar" }) {
  const [open, setOpen] = useState(false);
  if (!number) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`ligar-${number}`}
        className={`inline-flex items-center gap-1 text-blue hover:bg-tint rounded px-2 py-1 text-sm font-semibold ${className}`}
      >
        <Phone size={14} /> {label}
      </button>
      <WebphoneDialog open={open} number={number} onClose={() => setOpen(false)} />
    </>
  );
}
